import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import type { KycDocumentRequirement } from '#src/__generated__/globalTypes.ts';
import { iconAddressCard } from '#src/atoms/icons/system/iconAddressCard.ts';
import { iconCreditCardBlank } from '#src/atoms/icons/system/iconCreditCardBlank.ts';
import { FormControlLabel } from '#src/atoms/inputs/FormControlLabel/index.tsx';
import { BodyM, HeadlineL } from '#src/atoms/typography/index.tsx';
import { UploadInput } from '#src/components/fiatWallet/CreateFiatWallet/DocumentCheck/UploadInput/index.tsx';
import { StepWrapperFromProps } from '#src/components/fiatWallet/CreateFiatWallet/StepWrapper/index.tsx';
import { kycDocumentTypeLabelsWithPossessivePronoun } from '#src/components/fiatWallet/ProofOfIdentityForm/ChooseDocument/messages.ts';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { GraphqlForm } from '#src/components/form/Form/index.tsx';
import type { FileWithDataURL } from '#src/components/form/UploadFile/useUploadFile.ts';

const messages = defineMessages({
  front: {
    defaultMessage: 'front',
  },
  back: {
    defaultMessage: 'back',
  },
});

const StyledGraphqlForm = styled(GraphqlForm)`
  margin-bottom: 0;
  height: 100%;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  > * {
    width: 100%;
  }
`;

export type Props = {
  documentRequirement: KycDocumentRequirement;
  frontPage: FileWithDataURL | undefined;
  backPage: FileWithDataURL | undefined;
  setFrontPage: (file: FileWithDataURL) => void;
  setBackPage: (file: FileWithDataURL) => void;
  onBack: () => void;
  onSuccess: () => void;
  onSubmit: (onResult: (result: GraphQLResult) => void) => void;
};

const UploadFileWrapper = styled.div`
  display: flex;
  gap: var(--double-unit);
  align-items: flex-start;

  > * {
    flex-grow: 1;
    width: 50%;
  }
`;

export const Upload = ({
  documentRequirement,
  frontPage,
  backPage,
  setFrontPage,
  setBackPage,
  onSuccess,
  onSubmit,
  onBack,
}: Props) => {
  const { formatMessage } = useIntl();

  const filesCount = documentRequirement.nbPages;
  const filesUploadedCount = [frontPage, backPage].filter(Boolean).length;

  const formattedDocumentName = formatMessage(
    kycDocumentTypeLabelsWithPossessivePronoun[documentRequirement.documentType]
  );

  return (
    <StyledGraphqlForm
      onSuccess={onSuccess}
      onSubmit={(_, onResult) => {
        onSubmit(onResult);
      }}
      render={(Error, SubmitButton) => (
        <StepWrapperFromProps
          onBack={onBack}
          submitButton={
            <SubmitButton
              size="medium"
              disabled={filesUploadedCount !== filesCount}
            >
              <FormattedMessage defaultMessage="Submit ID" />
            </SubmitButton>
          }
        >
          <HeadlineL as="h3">
            <FormattedMessage
              defaultMessage="Upload {filesCount, plural, one {an image} other {images}} of {documentType}"
              values={{
                documentType: formattedDocumentName,
                filesCount,
              }}
            />
          </HeadlineL>
          <BodyM>
            <FormattedMessage
              defaultMessage="Make sure your photos aren’t blurry and the front of {documentType} clearly shows your face."
              values={{
                documentType: formattedDocumentName,
              }}
            />
          </BodyM>
          <Error />
          <UploadFileWrapper>
            <StyledFormControlLabel
              label={null}
              control={
                <UploadInput
                  name="frontPage"
                  documentName={
                    filesCount === 1
                      ? formattedDocumentName
                      : formatMessage(messages.front)
                  }
                  icon={iconAddressCard}
                  file={frontPage}
                  setFile={setFrontPage}
                />
              }
              labelPlacement="top"
            />
            {filesCount === 2 && (
              <StyledFormControlLabel
                label={null}
                control={
                  <UploadInput
                    name="backPage"
                    documentName={formatMessage(messages.back)}
                    icon={iconCreditCardBlank}
                    file={backPage}
                    setFile={setBackPage}
                  />
                }
                labelPlacement="top"
              />
            )}
          </UploadFileWrapper>
        </StepWrapperFromProps>
      )}
    />
  );
};
